import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { logoimages } from "@/data/logoimages";

// import dreamlandLogo from "../assets/logos/DreamlandLogo.png";
// import miranshLogo from "../assets/logos/miransh-logo.png";
// import shreelilaLogo from "../assets/logos/shrileela-logo_v2.png";



const  dreamlandLogo = logoimages.logos.landingpage.dreamlandLogo;
const  miranshLogo = logoimages.logos.landingpage.miranshLogo;
const  shreelilaLogo = logoimages.logos.landingpage.shreelilaLogo;

export default function HotelLoader({ hotel, visible, onDone }) {
  const config = {
    "dreamland-inn": {
      name: "Dreamland Inn",
      logo: dreamlandLogo,
      tag: "Our Flagship",
      type: "Signature Beach Resort",
      borderGradient: "linear-gradient(160deg, #5BA8D4 0%, #14649B 35%, #4A96C8 55%, #14649B 75%, #0D4D7A 100%)",
      glow: "rgba(20, 100, 155, 0.45)",
      tagColor: "#14649B",
    },
    "miransh-heavenly": {
      name: "Miransh Heavenly",
      logo: miranshLogo,
      tag: "Premium",
      type: "Luxury Cliff Retreat",
      borderGradient: "linear-gradient(160deg, #6DBFC2 0%, #337E81 35%, #5AB0B3 55%, #337E81 75%, #235759 100%)",
      glow: "rgba(51, 126, 129, 0.45)",
      tagColor: "#337E81",
    },
    "shree-lila": {
      name: "Shrileela",
      logo: shreelilaLogo,
      tag: "Warm & Welcoming",
      type: "Boutique Stay",
      borderGradient: "linear-gradient(160deg, #7A9468 0%, #405038 35%, #6A8459 55%, #405038 75%, #2C3827 100%)",
      glow: "rgba(64, 80, 56, 0.45)",
      tagColor: "#405038",
    },
  };

  const hotelId = hotel?.id ?? "dreamland-inn";
  const cfg = config[hotelId] ?? config["dreamland-inn"];

  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => {
      onDone(hotelId);
    }, 2500);
    return () => clearTimeout(timer);
  }, [visible, hotelId]);

  const GoldOrnament = () => (
    <svg width="56" height="14" viewBox="0 0 56 14" fill="none">
      <line x1="0" y1="7" x2="18" y2="7" stroke="#C8960C" strokeWidth="0.8" opacity="0.7" />
      <circle cx="21" cy="7" r="1.6" fill="#C8960C" opacity="0.75" />
      <circle cx="28" cy="7" r="2.4" fill="#C8960C" opacity="0.9" />
      <circle cx="35" cy="7" r="1.6" fill="#C8960C" opacity="0.75" />
      <line x1="38" y1="7" x2="56" y2="7" stroke="#C8960C" strokeWidth="0.8" opacity="0.7" />
    </svg>
  );

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="hotel-loader"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #060d1a 0%, #0F1F3D 60%, #081020 100%)",
          }}
        >
          {/* Glow orb */}
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.55, 0.3] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            style={{
              position: "absolute",
              width: "420px",
              height: "420px",
              borderRadius: "50%",
              background: `radial-gradient(circle, ${cfg.glow} 0%, transparent 70%)`,
              pointerEvents: "none",
            }}
          />

          {/* Pill Card */}
          <motion.div
            initial={{ opacity: 0, y: 55, scale: 0.82 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, ease: [0.23, 1, 0.32, 1] }}
          >
            {/* Border ring — pill shape */}
            <div
              style={{
                width: "210px",
                height: "340px",        // ← taller to suit pill proportions
                borderRadius: "9999px", // ← changed from "50% / 44%"
                padding: "3px",
                background: cfg.borderGradient,
              }}
            >
              {/* Inner card — pill shape */}
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "9999px", // ← changed from "50% / 44%"
                  overflow: "hidden",
                  background: "linear-gradient(180deg, #FFFFFF 0%, #FAF6EE 100%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  paddingTop: "48px",   // ← increased for pill headroom
                  paddingBottom: "42px",// ← increased for pill headroom
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  boxSizing: "border-box",
                  gap: "8px",
                }}
              >
                {/* Logo */}
                <motion.img
                  src={cfg.logo}
                  alt={cfg.name}
                  initial={{ scale: 0.65, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.25, duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
                  style={{
                    width: "74%",
                    maxHeight: "110px",
                    objectFit: "contain",
                    flexShrink: 0,
                  }}
                />

                {/* Tag pill */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.45 }}
                  style={{
                    padding: "3px 11px",
                    borderRadius: "9999px",
                    border: `1px solid ${cfg.tagColor}88`,
                    fontSize: "8px",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: cfg.tagColor,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {cfg.tag}
                </motion.div>

                {/* Hotel name */}
                <motion.p
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#0F1F3D",
                    textAlign: "center",
                    margin: 0,
                    lineHeight: 1.2,
                  }}
                >
                  {hotel?.name || cfg.name}
                </motion.p>

                {/* Hotel type */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  style={{
                    fontSize: "8px",
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: "#64748B",
                    fontWeight: 500,
                    margin: 0,
                    textAlign: "center",
                  }}
                >
                  {cfg.type}
                </motion.p>

                {/* Gold ornament */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  style={{ marginTop: "2px" }}
                >
                  <GoldOrnament />
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Progress bar + label */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55 }}
            style={{
              marginTop: "36px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "9px",
            }}
          >
            <div
              style={{
                width: "140px",
                height: "2px",
                background: "rgba(255,255,255,0.12)",
                borderRadius: "9999px",
                overflow: "hidden",
              }}
            >
              <motion.div
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 2.2, ease: "easeInOut", delay: 0.35 }}
                style={{
                  height: "100%",
                  background: "linear-gradient(90deg, #A6780A, #C8960C, #F5D87A, #C8960C)",
                  borderRadius: "9999px",
                }}
              />
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.4 }}
              transition={{ delay: 0.75 }}
              style={{
                color: "#F8FAFC",
                fontSize: "8.5px",
                letterSpacing: "0.26em",
                textTransform: "uppercase",
                margin: 0,
              }}
            >
              Preparing your experience
            </motion.p>
          </motion.div>

        </motion.div>
      )}
    </AnimatePresence>
  );
}